import React, { useState } from 'react';
import { X, BookOpen, CheckCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface WelcomeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImportComplete: () => void;
}

export function WelcomeModal({ isOpen, onClose, onImportComplete }: WelcomeModalProps) {
  const [importing, setImporting] = useState(false);
  const [imported, setImported] = useState(false);
  const [showSkipConfirm, setShowSkipConfirm] = useState(false);

  if (!isOpen) return null;

  const handleImportTestSermon = async () => {
    try {
      setImporting(true);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Create the example sermon
      const exampleContent = `<p><strong>Welcome to SermonBuild! This example sermon will walk you through how the editor works. Feel free to edit or delete it anytime.</strong></p>

<p><strong>Quick Start Tips:</strong></p>
<ul>
<li>Type <strong>/</strong> on an empty line to open the section menu — choose from Scripture, Main Point, Illustration, Quote, Application, and more</li>
<li>Press <strong>Escape</strong> to exit a sermon section and continue typing below it</li>
<li>Use <strong>Tab</strong> to indent text and <strong>Shift+Tab</strong> to outdent (up to 4 levels)</li>
<li>Drag sections by their handle (the grip icon on the left) to reorder them</li>
<li>Your work saves automatically as you type — no need to hit save</li>
<li>You can also import existing sermons from Word documents using the import button in the toolbar</li>
</ul>

<p>Everything outside of a colored section box is free-form text — perfect for personal notes, transitions, or speaking cues that only you see. Try typing below this line!</p>

<div class="sermon-section sermon-section-scripture" data-type="scripture" data-section-id="scripture-1">
<h3>Scripture Reading</h3>
<p><strong>John 3:16</strong> - "For God so loved the world that he gave his one and only Son, that whoever believes in him shall not perish but have eternal life."</p>
<p><em>Tip: This is a Scripture section. Each section type has its own color so you can scan your sermon at a glance. Click inside to edit, or press Escape to jump out.</em></p>
</div>

<p>You can write transition text, personal reminders, or stage directions between any sections. This area is completely flexible.</p>

<div class="sermon-section sermon-section-main-point" data-type="main-point" data-point-number="1" data-section-id="point-1">
<h3>Main Point 1: God's Love is Unconditional</h3>
<p>God's love for us doesn't depend on our performance or perfection. Even when we fall short, His love remains constant.</p>
<ul>
<li>John 3:16 reminds us that God's love is for the whole world</li>
<li>This love gives us security and peace</li>
<li>We can rest in knowing we are accepted and valued</li>
</ul>
<p><em>Tip: Hover over any section to see its toolbar — you can delete it, change its type, or drag it to a new position.</em></p>
</div>

<div class="sermon-section sermon-section-main-point" data-type="main-point" data-point-number="2" data-section-id="point-2">
<h3>Main Point 2: Grace Empowers Transformation</h3>
<p>God's grace doesn't just forgive us; it empowers us to live differently. Through His grace, we can overcome our struggles and grow in faith.</p>
<ul>
<li>Grace is unmerited favor from God</li>
<li>It provides strength for daily challenges</li>
<li>Grace enables us to extend forgiveness to others</li>
</ul>
</div>

<div class="sermon-section sermon-section-illustration" data-type="illustration" data-section-id="illustration-1">
<h3>Illustration: The Lighthouse</h3>
<p>A lighthouse doesn't ring bells or fire cannons to call attention to itself. It just shines its light consistently, guiding ships to safety. Similarly, our faith should be a steady light that guides others to Christ through our consistent love and service.</p>
</div>

<div class="sermon-section sermon-section-quote" data-type="quote" data-section-id="quote-1">
<h3>Quote</h3>
<p>"Grace is not simply leniency when we have sinned. Grace is the enabling gift of God not to sin. Grace is power, not just pardon." — John Piper</p>
<p><em>Tip: Use Quote sections for sermon quotes, book excerpts, or anything you want to attribute to a source.</em></p>
</div>

<div class="sermon-section sermon-section-application" data-type="application" data-section-id="application-1">
<h3>Application: Living Out Love</h3>
<p>This week, let's commit to:</p>
<ul>
<li>Showing kindness to someone who has hurt us</li>
<li>Offering help to a neighbor in need</li>
<li>Spending time in prayer, thanking God for His love</li>
</ul>
<p>Remember, small acts of love can have eternal impact.</p>
</div>

<div class="sermon-section sermon-section-prayer" data-type="prayer" data-section-id="prayer-1">
<h3>Closing Prayer</h3>
<p>Lord, thank You for Your unfailing love and amazing grace. Help us to live as Your children, reflecting Your love to the world around us. May our lives be a testimony of Your goodness. In Christ's name, Amen.</p>
</div>

<p><strong>More things to explore:</strong></p>
<ul>
<li><strong>Preach Mode</strong> — Click the "Preach" button in the toolbar for a distraction-free reading view with a built-in timer and adjustable font size</li>
<li><strong>AI Tools</strong> — Visit the Tools menu to analyze your sermon, generate study questions, create presentation slides, translate your sermon, and more</li>
<li><strong>Share</strong> — Share your sermon with other users or create a public study page link for your congregation</li>
<li><strong>Export</strong> — Download your sermon as a PDF or Word document from the toolbar</li>
<li><strong>Theological Assistant</strong> — Use the research assistant in the sidebar to look up commentary, cross-references, and background information as you write</li>
<li><strong>Format Profiles</strong> — Set default fonts, sizes, and line spacing in Settings so every new sermon starts with your preferred look</li>
</ul>`;

      const { data: newSermon, error: sermonError } = await supabase
        .from('sermons')
        .insert([
          {
            user_id: user.id,
            title: "Example Sermon: God's Amazing Love",
            scripture: "John 3:16",
            content: exampleContent,
            status: "complete",
            series_id: null,
            date: null,
            summary: "An example sermon demonstrating the editor's features with sections on God's unconditional love and transforming grace.",
            archived: false
          }
        ])
        .select()
        .single();

      if (sermonError) throw sermonError;

      setImported(true);
      setTimeout(() => {
        onImportComplete();
        onClose();
      }, 1500);

    } catch (error) {
      console.error('Error importing test sermon:', error);
      alert('Failed to import test sermon. Please try again.');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-6 border-b border-sand/20">
          <h2 className="text-xl font-semibold text-midnight">Welcome to SermonBuild!</h2>
          <button
            onClick={onClose}
            className="p-1 text-slate hover:text-midnight rounded-full"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="text-center">
            <BookOpen className="h-12 w-12 text-electric mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-midnight mb-2">
              Ready to start building sermons?
            </h3>
            <p className="text-slate mb-6">
              Import our example sermon to see how the editor works and explore all the features available to you.
            </p>
          </div>

          <div className="space-y-3">
            <button
              onClick={handleImportTestSermon}
              disabled={importing || imported}
              className="w-full flex items-center justify-center px-4 py-3 bg-electric text-white rounded-lg hover:bg-electric/90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {importing ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Importing Example Sermon...
                </>
              ) : imported ? (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Successfully Imported!
                </>
              ) : (
                <>
                  <BookOpen className="h-4 w-4 mr-2" />
                  Import Example Sermon
                </>
              )}
            </button>

            {showSkipConfirm ? (
              <div className="rounded-lg border border-sand/30 p-4 text-center space-y-3">
                <p className="text-sm text-slate">The example sermon walks you through all features in about 2 minutes. Skip anyway?</p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowSkipConfirm(false)}
                    className="flex-1 px-3 py-2 text-sm bg-electric text-white rounded-lg hover:bg-electric/90"
                  >
                    Show me
                  </button>
                  <button
                    onClick={onClose}
                    className="flex-1 px-3 py-2 text-sm text-slate hover:text-midnight border border-sand/30 rounded-lg"
                  >
                    Yes, skip
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setShowSkipConfirm(true)}
                className="w-full px-4 py-2 text-slate hover:text-midnight border border-sand/30 rounded-lg"
              >
                Skip for now
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}